import {
  AlertTriangleIcon,
  ArrowRightIcon,
  Box,
  Button,
  Card,
  CardBody,
  List,
  ListItem,
  ListItemText,
  Text,
} from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import React from 'react';
import { useRunPayrollDataContext } from '../contexts/RunPayrollDataContext';
import pluralize from 'pluralize';
import { ErrorWrapper, ErrorBackground } from './styles';
import { prepareUrlV2 } from 'utils/Urls';

const ExecutionWarningsCard = (): React.ReactElement | null => {
  const data = useRunPayrollDataContext();
  const autoSkippedCount = data.autoSkipCount ?? 0;
  const warningCount = data.negativeNetPayWarningCount ?? 0;
  const negativeSAWarningCount = data.negativeSAWarningCount ?? 0;
  const pfMismatchWarningCount = data.pfMismatchWarningCount ?? 0;
  const totalEmployees = autoSkippedCount + warningCount + negativeSAWarningCount + pfMismatchWarningCount;

  // Build list items array only when counts change
  const listItems = React.useMemo(() => {
    const items: React.ReactElement[] = [];

    if (autoSkippedCount > 0) {
      items.push(
        <ListItem key="auto-skipped">
          <ListItemText color="surface.text.gray.subtle">
            {autoSkippedCount} {pluralize('employee', autoSkippedCount)} auto-skipped
          </ListItemText>
        </ListItem>,
      );
    }

    if (warningCount > 0) {
      items.push(
        <ListItem key="negative-pay">
          <ListItemText color="surface.text.gray.subtle">
            {warningCount} {pluralize('employee', warningCount)} {pluralize('has', warningCount)}{' '}
            negative net pay
          </ListItemText>
        </ListItem>,
      );
    }

    if (negativeSAWarningCount > 0) {
      items.push(
        <ListItem key="negative-sa">
          <ListItemText color="surface.text.gray.subtle">
            {negativeSAWarningCount} {pluralize('employee', negativeSAWarningCount)}{' '}
            {pluralize('has', negativeSAWarningCount)} negative special allowance
          </ListItemText>
        </ListItem>,
      );
    }

    if (pfMismatchWarningCount > 0) {
      items.push(
        <ListItem key="pf-mismatch">
          <ListItemText color="surface.text.gray.subtle">
            {pfMismatchWarningCount} {pluralize('employee', pfMismatchWarningCount)}{' '}
            {pluralize('has', pfMismatchWarningCount)} PF contribution mismatch
          </ListItemText>
        </ListItem>,
      );
    }

    return items;
  }, [autoSkippedCount, warningCount, negativeSAWarningCount, pfMismatchWarningCount]);

  // Don't render the card if there are no employees to show
  if (totalEmployees === 0) {
    return null;
  }

  return (
    <ErrorWrapper>
      <Card padding="spacing.0" width="100%">
        <CardBody>
          <ErrorBackground>
            <Box display="flex" flexDirection="column" gap="spacing.6">
              <Box display="flex" flexDirection="column" gap="spacing.3">
                <Box display="flex" flexDirection="row" gap="spacing.3" alignItems="center">
                  <AlertTriangleIcon size="medium" color="interactive.icon.notice.normal" />
                  <Text size="medium" weight="semibold" color="interactive.text.notice.normal">
                    Review {totalEmployees} {pluralize('employee', totalEmployees)}
                  </Text>
                </Box>
                <Box display="flex" flexDirection="column" gap="spacing.3">
                  <List>{listItems}</List>
                </Box>
              </Box>
              <Button
                isFullWidth={false}
                variant="tertiary"
                icon={ArrowRightIcon}
                iconPosition="right"
                size="medium"
                href={prepareUrlV2(routePaths.customReport.root, {
                  type: '14',
                  payrollMonth: data.payrollMonth,
                })}
                target="_blank"
                testID="view-warning-details-button">
                View details
              </Button>
            </Box>
          </ErrorBackground>
        </CardBody>
      </Card>
    </ErrorWrapper>
  );
};

export default ExecutionWarningsCard;
